import './index.scss';
import React, { useRef, useState, useMemo, useLayoutEffect, useEffect, useCallback } from 'react';
import { IfShow, TableSkeleton, If } from 'common/src/components';
import { PaginationV2 } from '../pagination';
import { Table } from '@umu/design-base';
import { IPropsTable, ISelectionsCheckInfo } from './interface';
import { CustomScrollBar } from './components';
import { getTableConfig, TABLE_SCROLL_CLS_STICKY, TABLE_SCROLL_CLS_NO_STICKY } from './dataFlow';
import { defaultEmptyText, getColumns } from './dataFlow/element';
import { useHover, useInViewport, useScroll, useSize } from 'ahooks';
import { $document } from '@umu/umu-dom';
import { useSelections } from 'ahooks';
import _get from 'lodash/get';

const CustomAdvancedTable = <T extends object>(props: IPropsTable<T>) => {
    const {
        columns: defaultColumns,
        list = [],
        skeletonInfo,
        isLoading = false,
        className = '',
        pageInfo,
        emptyText = defaultEmptyText,
        sticky = false,
        onPageChange,
        tableCls = '',
        checkKey = 'id',
        offsetX = 0,
        scroll = { x: 1200 },
        onCheckChange,
        style = {},
        rowClassName,
    } = props;
    const tableScrollCls = tableCls || (sticky ? TABLE_SCROLL_CLS_STICKY : TABLE_SCROLL_CLS_NO_STICKY);
    const { tableConfig, viewsConfig, scrollWidth } = getTableConfig(props);
    // 初始位置状态
    const [tableWidth, setTableWidth] = useState(0);
    const [isShowTable, setIsShowTable] = useState(false);
    const [columns, setColumns] = useState(defaultColumns);
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollTopRef = useRef<HTMLDivElement>(null);
    const scrollBottomRef = useRef<HTMLDivElement>(null);
    const customAdvancedTableRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const size = useSize(tableRef?.current?.querySelector('table'));
    const bodySize = useSize($document.body);
    const checkInfo: ISelectionsCheckInfo<string> = useSelections(list.map((item) => _get(item, checkKey) as string));
    const isHoverIng = useHover(customAdvancedTableRef);
    const [inViewport] = useInViewport(scrollBottomRef);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    useScroll(tableRef?.current?.querySelector(tableScrollCls), (scroll) => {
        if (!tableWidth) {
            return false;
        }
        let scrollLeft = scroll?.left || 0;
        scrollLeft = (scrollLeft * scrollWidth) / tableWidth;
        scrollTopRef.current && (scrollTopRef.current.style.left = `${scrollLeft}px`);
        scrollBottomRef.current && (scrollBottomRef.current.style.left = `${scrollLeft}px`);
        return false;
    });
    const { isShowScrollBar, styleScroll } = useMemo(() => {
        const scrollBarWidth = tableWidth > 0 ? (scrollWidth / tableWidth) * scrollWidth : 0;
        return {
            isShowScrollBar: tableWidth > scrollWidth + offsetX && scrollWidth > 0,
            styleScroll: {
                width: scrollBarWidth,
            },
        };
    }, [tableWidth, scrollWidth, tableWidth]);

    useEffect(() => {
        onCheckChange?.(checkInfo.selected);
    }, [checkInfo.selected]);
    useLayoutEffect(() => {
        setIsShowTable(!isLoading);
        size?.width && setTableWidth(size?.width);
    }, [isLoading, size, columns, bodySize]);
    const onCancelSelectAll = () => {
        checkInfo.unSelectAll();
        onCheckChange?.([]);
    };
    useEffect(() => {
        // 获取customAdvancedTableRef下  line-clamp 的样式元素 并依据 data-clamp 属性设置行数
        const lineClampList = customAdvancedTableRef.current?.querySelectorAll('.line-clamp') || [];
        lineClampList?.forEach((item: any) => {
            const clamp = item?.getAttribute('data-clamp') || 2;
            if (clamp) {
                item.style.webkitLineClamp = clamp;
            }
        });
        onCancelSelectAll();
    }, [list]);
    /*
        页面渲染完成后遍历 columns 下是否存在 autoWidth 为 true 的列，如果存在则获取 widthClassName 对应的所有元素并获取到最大宽度 将最大宽度设置到对应的列上重新渲染表格
        临时方案： 后续在看是否有更好的解决方案
    */
    useEffect(() => {
        const updateColumnWidths = () => {
            columns.forEach((item) => {
                if (item.autoWidth) {
                    const widthClassName = item.widthClassName;
                    const widthList = customAdvancedTableRef.current?.querySelectorAll(
                        `.${widthClassName}`,
                    ) as unknown as HTMLElement[];
                    let maxWidth = 0;
                    widthList?.forEach((widthItem: HTMLElement) => {
                        const width = widthItem?.offsetWidth || 0;
                        if (width > maxWidth) {
                            maxWidth = width;
                        }
                    });
                    item.width = maxWidth;
                }
            });
        };

        if (isShowTable) {
            updateColumnWidths();
            setColumns([...columns]);
        } else {
            setColumns([...defaultColumns]);
        }
    }, [defaultColumns, size, isShowTable]);
    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        e.preventDefault();
        const tableInfo = tableRef?.current?.querySelector(tableScrollCls) as HTMLElement;
        if (tableInfo) {
            const offsetLeft = tableInfo?.offsetLeft || 0;

            startX.current = e.pageX - offsetLeft;
            scrollLeft.current = tableInfo.scrollLeft;
        }
    };

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging.current) {
            return;
        }
        // 阻止默认事件 避免文字选中
        e.preventDefault();
        const tableInfo = tableRef?.current?.querySelector(tableScrollCls) as HTMLElement;
        if (tableInfo) {
            const offsetLeft = tableInfo?.offsetLeft || 0;
            const x = e.pageX - offsetLeft;
            const walk = x - startX.current; // 计算鼠标移动的距离
            tableInfo.scrollLeft = scrollLeft.current + walk; // 更新滚动条位置
        }
    }, []);
    const onScrollClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isDragging.current) {
            return;
        }
        handleMouseMove(e.nativeEvent);
    };
    useEffect(() => {
        // 监听鼠标抬起事件
        // 监听鼠标移动事件
        // 监听鼠标按下事件
        $document.addEventListener('mouseup', handleMouseUp);
        $document.addEventListener('mousemove', handleMouseMove);
        return () => {
            $document.removeEventListener('mouseup', handleMouseUp);
            $document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);
    return (
        <div
            ref={customAdvancedTableRef}
            className={`${className} custom-advanced-table custom-advanced-table-${sticky ? 'sticky' : 'no-sticky'}`}
            style={style}
            onMouseUp={handleMouseUp}
            onMouseMove={(e) => handleMouseMove(e.nativeEvent)}
        >
            <IfShow show={!isShowTable}>
                <TableSkeleton columns={columns.length} count={8} showStripes {...skeletonInfo} />
            </IfShow>
            <IfShow show={isShowTable}>
                <div {...viewsConfig}>
                    <div className="scroll-bar-top-info-placeholder">
                        <div
                            className="scroll-bar-info scroll-bar-top-info"
                            style={{
                                visibility:
                                    isShowScrollBar && (isHoverIng || isDragging.current) && !inViewport
                                        ? 'visible'
                                        : 'hidden',
                            }}
                        >
                            <CustomScrollBar
                                styleScroll={styleScroll}
                                scrollRef={scrollTopRef}
                                onClick={onScrollClick}
                                onMouseDown={handleMouseDown}
                            />
                        </div>
                    </div>
                    <div {...viewsConfig} ref={tableRef}>
                        <Table
                            className="custom-advanced-table-zone"
                            rowClassName={(record, index) => {
                                const rowCls = rowClassName?.(record, index) || '';
                                return `${rowCls} ${index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}`;
                            }}
                            columns={getColumns(columns, { checkInfo, checkKey })}
                            {...tableConfig}
                            scroll={scroll}
                            pagination={false}
                            dataSource={list}
                        />
                        <If condition={list.length === 0 && !!emptyText}>
                            <div className="table-empty-info">{emptyText}</div>
                        </If>
                    </div>
                    <If condition={!!pageInfo && pageInfo?.total_page_num > 1}>
                        <div className="custom-advanced-table-pagination">
                            <PaginationV2
                                showSizeChanger
                                currentPage={pageInfo?.current_page || 1}
                                maxPage={pageInfo?.total_page_num || 1}
                                onPageChange={onPageChange}
                            />
                        </div>
                    </If>
                </div>
            </IfShow>
        </div>
    );
};
export { CustomAdvancedTable };
